/*
 * Lazy tab panes: a tab button carrying data-tab-url fetches its pane the
 * first time it is shown. One document-level listener on Bootstrap's
 * show.bs.tab (it bubbles) replaces the per-call jQuery .on() binding, which
 * accumulated a duplicate handler every time a detail modal opened.
 */
import { request } from './http';
import { setSpinner, swapHtml, one } from './dom';
import { setAlertMessage } from './alert';
import { applyUtcToInputs } from './forms';

let bound = false;

export function isLoaded(tab: Element): boolean {
    return tab.getAttribute('data-loaded') === 'true';
}

export function markLoaded(tab: Element, loaded = true): void {
    tab.setAttribute('data-loaded', loaded ? 'true' : 'false');
}

/** Load a lazy tab's pane if it has not been loaded yet. */
export async function loadTab(tab: Element): Promise<void> {
    if (isLoaded(tab)) return;
    const tabName = tab.getAttribute('data-tab') ?? '';
    const target = tab.getAttribute('data-bs-target') ?? '#';
    const tabUrl = tab.getAttribute('data-tab-url');
    if (!tabUrl || !one(target)) return;

    setSpinner(target);
    try {
        const result = await request(tabUrl, { accept: 'html' });
        swapHtml(target, result.text, 'replace');
        markLoaded(tab);
        applyUtcToInputs(target);
        one(target)?.dispatchEvent(new CustomEvent('mcp:tab-loaded', { bubbles: true, detail: { tab, url: tabUrl } }));
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setAlertMessage(target, 'danger', `${tabName} Detail Action failed to execute`, message);
    }
}

/** The show.bs.tab handler; exported for the legacy Mcp.Admin.AjaxTabClick name. */
export function onShowTab(event: Event): void {
    const tab = event.target;
    if (tab instanceof Element && tab.hasAttribute('data-tab-url')) void loadTab(tab);
}

/** Install the delegated listener once; safe to call repeatedly. */
export function initTabs(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('show.bs.tab', onShowTab);
}

/** Programmatically show a tab (the legacy `$(tab).click()` pattern). */
export function showTab(target: string | Element): void {
    const tab = typeof target === 'string' ? one(target) : target;
    if (!tab) return;
    const Tab = window.bootstrap?.Tab;
    if (Tab) Tab.getOrCreateInstance(tab).show();
    else (tab as HTMLElement).click();
}

/**
 * A tab strip marked `data-mcp-autotab` opens its first lazy tab as soon as
 * it lands in the page -- the replacement for the per-view
 * `$(document).ready(function(){ $('#FirstTab').click(); })` initialiser,
 * which never fired for a partial swapped into the SPA shell (a @section
 * scripts block belongs to the layout, not to the partial).
 *
 * Registered as an enhancer, so it covers the first page load, an AJAX swap
 * and a modal alike. The marker attribute is stamped once so a re-enhanced
 * subtree does not fight the operator's own tab choice.
 *
 * A strip that renders one of its tabs ALREADY active is asking for that tab,
 * not the first one, and it needs loading a different way: Bootstrap's
 * Tab.show() returns early on a tab that is already active, so it would never
 * fire show.bs.tab and the pane would sit on its placeholder for ever. The
 * License Detail modal is the case -- it lands on the Domain Binder tab
 * instead of Properties when the license has no coverage configured, which is
 * the one time the operator needs that tab first.
 */
export function autoOpenTabs(root: ParentNode): void {
    const strips: Element[] = [];
    if (root instanceof Element && root.matches('[data-mcp-autotab]')) strips.push(root);
    strips.push(...Array.from(root.querySelectorAll('[data-mcp-autotab]')));
    for (const strip of strips) {
        if (strip.getAttribute('data-mcp-autotab-done') === 'true') continue;
        strip.setAttribute('data-mcp-autotab-done', 'true');
        const active = strip.querySelector('.active[data-tab-url]');
        if (active) {
            void loadTab(active);
            continue;
        }
        const first = strip.querySelector('[data-tab-url]');
        if (first) showTab(first);
    }
}
